import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarPlus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { date: "", title: "", city: "", time: "", ticketUrl: "", note: "" };
const inputCls =
  "h-11 w-full border border-neutral-800 bg-black/40 px-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-red-700 focus:outline-none";

const fmt = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

function ConcertForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.title.trim() || !form.city.trim()) {
      toast("Data, titolo e città sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form data-testid="concert-form" onSubmit={submit} className="border border-red-900/40 bg-[#0D0D0D] p-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <input data-testid="concert-date-input" type="date" value={form.date} onChange={set("date")} className={inputCls} />
        <input data-testid="concert-title-input" value={form.title} onChange={set("title")} placeholder="Titolo / locale *" className={`${inputCls} sm:col-span-2`} />
        <input data-testid="concert-city-input" value={form.city} onChange={set("city")} placeholder="Città *" className={inputCls} />
        <input data-testid="concert-time-input" value={form.time} onChange={set("time")} placeholder="Orario (es. 22:00)" className={inputCls} />
        <input data-testid="concert-ticket-input" value={form.ticketUrl} onChange={set("ticketUrl")} placeholder="Link biglietti (opzionale)" className={inputCls} />
        <input data-testid="concert-note-input" value={form.note} onChange={set("note")} placeholder="Nota (es. ingresso libero)" className={`${inputCls} sm:col-span-3`} />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button data-testid="concert-save-button" type="submit" disabled={saving} className="bg-[#C8102E] px-5 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-white hover:bg-[#FF2A4B] disabled:opacity-60">
          {saving ? "Salvo…" : "Salva data"}
        </button>
        <button data-testid="concert-cancel-button" type="button" onClick={onCancel} className="inline-flex items-center gap-2 border border-neutral-800 px-4 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-red-500">
          <X className="h-3.5 w-3.5" /> Annulla
        </button>
      </div>
    </form>
  );
}

export default function ConcertsAdmin({ headers, onUnauthorized }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | concert

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/concerts`);
      setRows(data);
    } catch {
      toast("Impossibile caricare le date");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const handle = async (fn, okMsg) => {
    try {
      await fn();
      toast(okMsg);
      setEditing(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast("Operazione non riuscita");
    }
  };

  const save = (form) =>
    editing === "new"
      ? handle(() => axios.post(`${API}/api/admin/concerts`, form, { headers: headers() }), "Data aggiunta")
      : handle(() => axios.put(`${API}/api/admin/concerts/${editing.id}`, form, { headers: headers() }), "Data aggiornata");

  const remove = (c) => {
    if (!window.confirm(`Eliminare "${c.title}" — ${c.city}?`)) return;
    handle(() => axios.delete(`${API}/api/admin/concerts/${c.id}`, { headers: headers() }), "Data eliminata");
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-400">Le date appaiono subito sul sito, divise in prossime e passate.</p>
        {editing === null && (
          <button data-testid="concert-add-button" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 bg-[#C8102E] px-4 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-white hover:bg-[#FF2A4B]">
            <CalendarPlus className="h-4 w-4" /> Nuova data
          </button>
        )}
      </div>
      {editing !== null && (
        <div className="mt-5">
          <ConcertForm initial={editing === "new" ? EMPTY : { ...EMPTY, ...editing }} onSave={save} onCancel={() => setEditing(null)} />
        </div>
      )}
      <div data-testid="admin-concerts-table" className="mt-6 overflow-x-auto border border-neutral-800 bg-[#0D0D0D]">
        <table className="w-full text-left text-sm">
          <thead className="font-mono-tac text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            <tr className="border-b border-neutral-800">
              <th className="px-5 py-3">Data</th>
              <th className="px-5 py-3">Titolo</th>
              <th className="px-5 py-3">Città</th>
              <th className="px-5 py-3">Stato</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows?.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500">Nessuna data.</td></tr>
            )}
            {rows
              ?.slice()
              .sort((a, b) => b.date.localeCompare(a.date))
              .map((c) => (
                <tr key={c.id} data-testid="admin-concert-row" className="border-b border-neutral-800/60 last:border-b-0">
                  <td className="whitespace-nowrap px-5 py-3 text-neutral-100">{fmt(c.date)}{c.time ? ` · ${c.time}` : ""}</td>
                  <td className="px-5 py-3 text-neutral-200">{c.title}</td>
                  <td className="px-5 py-3 text-neutral-400">{c.city}</td>
                  <td className="px-5 py-3">
                    <span className={`font-mono-tac text-[10px] uppercase tracking-[0.2em] ${c.date >= today ? "text-red-500" : "text-neutral-600"}`}>
                      {c.date >= today ? "Prossima" : "Passata"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    <button data-testid="admin-concert-edit" onClick={() => setEditing(c)} aria-label={`Modifica ${c.title}`} className="mr-3 text-neutral-500 hover:text-red-500"><Pencil className="h-4 w-4" /></button>
                    <button data-testid="admin-concert-delete" onClick={() => remove(c)} aria-label={`Elimina ${c.title}`} className="text-neutral-600 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
